import {
  ContextType,
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect
} from 'react';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import { highlightCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/highlightHandler';
import { isPdfPageRendered } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { getAssetAnnotationZoom } from '@pages/MediaViewer/AssetViewer/Canvas/utils/annotationCoordinates';
import {
  getScrollLayoutMargin,
  layoutAnnotationCanvas
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/annotationLayout';
import { FabricContext } from '@context/FabricContext';
import { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';

type UsePdfFabricCanvasParams = {
  fabricContext: ContextType<typeof FabricContext>;
  wrapperRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  contentRef: RefObject<HTMLDivElement>;
  canvasContainerRef: RefObject<HTMLDivElement>;
  pageRefsMap: MutableRefObject<Map<number, HTMLDivElement>>;
  activePageNumber: number;
  zoomFactor: number;
  // Pages are rotated with CSS: laid out again once rotated
  turnoverDegree: number;
  isPageSizeReady: boolean;
  selectedCommentId: string | null;
  pageRefsVersion: number;
  pagesReadyVersion: number;
  isMainCanvasVisible: boolean;
  /** Shown without taking taps, e.g. a viewed comment over other pins */
  isMainCanvasPassThrough?: boolean;
  selectComment: IAssetViewerStateContextValue['selectComment'];
  setCanvasMode: IAssetViewerStateContextValue['setCanvasMode'];
  setDocumentHighlight: IAssetViewerStateContextValue['setDocumentHighlight'];
};

export function usePdfFabricCanvas({
  fabricContext,
  wrapperRef,
  contentRef,
  canvasContainerRef,
  pageRefsMap,
  activePageNumber,
  zoomFactor,
  turnoverDegree,
  isPageSizeReady,
  selectedCommentId,
  pageRefsVersion,
  pagesReadyVersion,
  isMainCanvasVisible,
  isMainCanvasPassThrough = false,
  selectComment,
  setCanvasMode,
  setDocumentHighlight
}: UsePdfFabricCanvasParams) {
  const setFabricCanvasVisible = useCallback(
    (visible: boolean) => {
      const canvasContainer = canvasContainerRef.current;
      if (!canvasContainer) return;
      canvasContainer.style.visibility = visible ? 'visible' : 'hidden';
      canvasContainer.style.pointerEvents =
        visible && !isMainCanvasPassThrough ? '' : 'none';
    },
    [canvasContainerRef, isMainCanvasPassThrough]
  );

  const syncFabricCanvas = useCallback((): boolean => {
    const canvasContainer = canvasContainerRef?.current;
    const content = contentRef?.current;
    if (!canvasContainer || !content || !fabricContext.canvas) return false;

    const pageEl = pageRefsMap.current.get(activePageNumber);
    if (!pageEl || !isPdfPageRendered(pageEl)) return false;

    const targetEl =
      (pageEl.querySelector('.media_viewer_pdf-inner') as HTMLElement | null) ??
      pageEl;

    const rect = targetEl.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    content.style.removeProperty('width');
    content.style.removeProperty('height');

    // Over the visible part of the page (and around it, so scrolling a bit
    // does not lay it out again), in the content's coordinates: it scrolls
    // with the page
    const contentRect = content.getBoundingClientRect();
    const origin = {
      left: contentRect.left + content.clientLeft,
      top: contentRect.top + content.clientTop
    };
    const toContent = (clientRect: DOMRect) => ({
      left: clientRect.left - origin.left,
      top: clientRect.top - origin.top,
      width: clientRect.width,
      height: clientRect.height
    });
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    const visibleRect = viewport?.getBoundingClientRect() ?? rect;
    layoutAnnotationCanvas({
      canvas: fabricContext.canvas,
      element: canvasContainer,
      asset: toContent(rect),
      visible: toContent(visibleRect),
      zoom: getAssetAnnotationZoom(zoomFactor),
      margin: getScrollLayoutMargin(visibleRect),
      snapOrigin: origin
    });
    return true;
  }, [
    canvasContainerRef,
    contentRef,
    wrapperRef,
    pageRefsMap,
    activePageNumber,
    zoomFactor,
    fabricContext.canvas,
    pageRefsVersion
  ]);

  const runCanvasLayout = useCallback(() => {
    if (!fabricContext.canvas) return;

    if (!isPageSizeReady || !isMainCanvasVisible) {
      setFabricCanvasVisible(false);
      return;
    }

    const synced = syncFabricCanvas();
    if (!synced) {
      setFabricCanvasVisible(false);
      return;
    }

    setFabricCanvasVisible(true);
    fabricContext.canvas.requestRenderAll();
  }, [
    isPageSizeReady,
    isMainCanvasVisible,
    fabricContext.canvas,
    syncFabricCanvas,
    setFabricCanvasVisible
  ]);

  const onClearHighlightObjects = useCallback(() => {
    const { canvas } = fabricContext;
    if (!canvas) return;
    const getHighlightObjects = canvas
      .getObjects()
      .filter((item) => item.type === 'highlight');
    getHighlightObjects.forEach((obj) => canvas.remove(obj));
    canvas.fire('object:history:remove');
  }, [fabricContext]);

  const renderTextHighlights = useCallback(
    (rects: any) => {
      if (!fabricContext.canvas) return;
      syncFabricCanvas();
      onClearHighlightObjects();
      highlightCreator({
        canvas: fabricContext.canvas,
        documentHighlightRects: rects,
        viewerActions: {
          selectComment,
          setCanvasMode,
          setDocumentHighlight
        }
      });
    },
    [
      fabricContext.canvas,
      syncFabricCanvas,
      onClearHighlightObjects,
      selectComment,
      setCanvasMode,
      setDocumentHighlight
    ]
  );

  useEffect(() => {
    runCanvasLayout();

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    viewport?.addEventListener('scroll', runCanvasLayout, {
      passive: true
    });
    window.addEventListener('resize', runCanvasLayout);

    return () => {
      viewport?.removeEventListener('scroll', runCanvasLayout);
      window.removeEventListener('resize', runCanvasLayout);
    };
  }, [wrapperRef, runCanvasLayout]);

  useLayoutEffect(() => {
    runCanvasLayout();
  }, [
    selectedCommentId,
    activePageNumber,
    pageRefsVersion,
    pagesReadyVersion,
    zoomFactor,
    turnoverDegree,
    isMainCanvasVisible,
    runCanvasLayout
  ]);

  return {
    setFabricCanvasVisible,
    syncFabricCanvas,
    runCanvasLayout,
    onClearHighlightObjects,
    renderTextHighlights
  };
}
